import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Injector, Input, Output, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { KeysPipe } from '../../shared/keys.pipe';
import { TreeItem, TreeLevel } from '../../tree.model';
import { TreeInteractionService } from '../../tree-interaction.service';
import { UndoRedoService } from '../../undo-redo.service';
import propertiesMapData from '../properties-map.json';

type PropertyValueMap = Record<string, any[]>;
type RoutePropertyMap = Record<string, PropertyValueMap | Record<string, PropertyValueMap>>;
type PropertiesMap = Record<string, RoutePropertyMap>;

interface PropertySuggestion {
  key: string;
  value: any;
  values: any[];
}

@Component({
  selector: 'app-properties-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, KeysPipe],
  templateUrl: './properties-panel.component.html',
  styleUrl: './properties-panel.component.scss'
})
export class PropertiesPanelComponent {
  private readonly propertiesMap = propertiesMapData as unknown as PropertiesMap;

  @Input() metadata: any;
  @Input() sourceItems: TreeItem[] = [];
  @Input() targetItems: TreeItem[] = [];
  @Input() isViewingDiff = false;

  @Output() metadataChange = new EventEmitter<any>();

  selectedPropItem: TreeItem | null = null;
  editingProperties: { key: string, value: any }[] = [];
  propertySuggestionValues: Record<string, any> = {};
  isEditingProperties = false;

  constructor(
    private interactionService: TreeInteractionService,
    private undoRedoService: UndoRedoService,
    private injector: Injector
  ) {
    effect(() => {
      const req = this.interactionService.propertiesItem();
      if (req) {
        this.selectProperties(req);
      } else {
        this.resetState();
      }
    }, { injector: this.injector });
  }

  openMetadataProperties(event: MouseEvent) {
    if (!this.metadata) {
      console.warn('Metadata is missing, cannot open properties');
      return;
    }

    event.stopPropagation();

    const metadataItem: TreeItem = {
      id: 'metadata',
      name: this.metadata.name || 'Metadata',
      level: TreeLevel.Metadata,
      children: [],
      position: 0,
      item: {
        properties: {
          name: this.metadata.name,
          type: this.metadata.type,
          ...this.metadata
        }
      } as any
    };

    this.selectProperties({ item: metadataItem, event });
  }

  selectProperties(data: { item: TreeItem, event?: MouseEvent }) {
    this.selectedPropItem = data.item;
    this.isEditingProperties = false;
    this.editingProperties = [];
    this.propertySuggestionValues = {};
  }

  onPropertiesPopoverBeforeToggle(event: Event) {
    const toggleEvent = event as ToggleEvent;
    if (toggleEvent.newState === 'closed') {
      this.interactionService.closeProperties();
      this.resetState();
    }
  }

  enableEditMode(event: MouseEvent | null) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.isEditingProperties = true;
    this.editingProperties = [];
    this.propertySuggestionValues = {};

    try {
      const props = this.currentProperties;
      Object.keys(props).forEach(key => {
        this.editingProperties.push({ key, value: props[key] });
      });
    } catch (e) {
      console.error('Error in enableEditMode', e);
    }
  }

  saveProperties() {
    if (!this.selectedPropItem) {
      return;
    }

    this.undoRedoService.notifyBeforeMutation();

    const newProperties: Record<string, any> = {};
    for (const prop of this.editingProperties) {
      if (prop.key && prop.key.trim() !== '') {
        newProperties[prop.key] = prop.value;
      }
    }

    if (this.selectedPropItem.id === 'metadata') {
      const metadata = { ...this.metadata, ...newProperties };
      if (newProperties['name']) metadata.name = newProperties['name'];
      if (newProperties['type']) metadata.type = newProperties['type'];

      this.metadata = metadata;
      this.metadataChange.emit(metadata);
      this.selectedPropItem.updatedProperties = metadata;
      this.isEditingProperties = false;
      this.propertySuggestionValues = {};
      return;
    }

    this.selectedPropItem.updatedProperties = newProperties;

    const originalProps = (this.selectedPropItem.item as any)?.properties || {};
    const isDiff = JSON.stringify(newProperties) !== JSON.stringify(originalProps);

    this.selectedPropItem.propertiesUpdated = isDiff;
    this.selectedPropItem.isUpdated = this.selectedPropItem.isNew || isDiff || !!this.selectedPropItem.updatedText;

    if (!isDiff) {
      this.selectedPropItem.updatedProperties = undefined;
    }

    this.isEditingProperties = false;
    this.propertySuggestionValues = {};
  }

  addProperty() {
    this.editingProperties.push({ key: '', value: '' });
  }

  addSuggestedProperty(suggestion: PropertySuggestion, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.hasEditingProperty(suggestion.key)) {
      return;
    }

    this.editingProperties.push({
      key: suggestion.key,
      value: this.propertySuggestionValues[suggestion.key] ?? suggestion.value
    });
  }

  removeProperty(index: number, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.editingProperties.splice(index, 1);
  }

  cancelProperties(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.isEditingProperties = false;
    this.propertySuggestionValues = {};
  }

  get currentProperties(): Record<string, any> {
    if (!this.selectedPropItem) {
      return {};
    }

    return this.selectedPropItem.updatedProperties || (this.selectedPropItem.item as any)?.properties || {};
  }

  get hasCurrentProperties(): boolean {
    return Object.keys(this.currentProperties).length > 0;
  }

  getPropertySuggestions(): PropertySuggestion[] {
    const propertyMap = this.getPropertyMapForSelectedItem();
    if (!propertyMap) {
      return [];
    }

    console.log("have property map", propertyMap)

    return Object.keys(propertyMap)
      .filter(key => !this.hasEditingProperty(key))
      .map(key => {
        const values = propertyMap[key] || [];
        const selectedValue = this.propertySuggestionValues[key] ?? values[0] ?? '';

        return {
          key,
          value: selectedValue,
          values
        };
      });
  }

  hasPropertySuggestions(): boolean {
    return this.getPropertySuggestions().length > 0;
  }

  private resetState() {
    this.selectedPropItem = null;
    this.editingProperties = [];
    this.propertySuggestionValues = {};
    this.isEditingProperties = false;
  }

  private hasEditingProperty(key: string): boolean {
    return this.editingProperties.some(prop => prop.key === key);
  }

  private getPropertyMapForSelectedItem(): PropertyValueMap | null {
    if (!this.selectedPropItem || this.selectedPropItem.level === TreeLevel.Metadata) {
      return null;
    }

    const routeName = this.getRouteNameForItem(this.selectedPropItem);
    if (!routeName) {
      return null;
    }

    const routeProperties = this.propertiesMap[routeName];

    if (!routeProperties) {
      return null;
    }

    const itemType = ((this.selectedPropItem.item as any)?.type ?? this.selectedPropItem.tag ?? '').toString();

    if (this.selectedPropItem.level === TreeLevel.Route) {
      return (routeProperties['_section'] as PropertyValueMap) || null;
    }

    if (this.selectedPropItem.level === TreeLevel.Component || this.selectedPropItem.level === TreeLevel.SubComponent) {
      return (routeProperties[itemType] as PropertyValueMap) || null;
    }

    if (this.selectedPropItem.level === TreeLevel.Element || this.selectedPropItem.level === TreeLevel.SubComponentElement) {
      const elementProperties = routeProperties['_elements'] as Record<string, PropertyValueMap> | undefined;
      return elementProperties?.[itemType] || null;
    }

    return null;
  }

  private getRouteNameForItem(item: TreeItem): string | null {
    if (item.level === TreeLevel.Route) {
      return ((item.item as any)?.route ?? item.tag ?? null) as string | null;
    }

    const route = this.findParentRoute(item);
    return route ? (((route.item as any)?.route ?? route.tag ?? null) as string | null) : null;
  }

  private findParentRoute(item: TreeItem): TreeItem | null {
    const routes = [...(this.sourceItems || []), ...(this.targetItems || [])];

    for (const route of routes) {
      if (route.id === item.id || this.treeContainsItem(route, item.id)) {
        return route;
      }
    }

    return null;
  }

  private treeContainsItem(parent: TreeItem, itemId: string): boolean {
    return (parent.children || []).some(child => child.id === itemId || this.treeContainsItem(child, itemId));
  }
}
